import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight, Loader2, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { partnerApi, type TenantsResponse } from '../../lib/api';
import { PageHeader } from '../../components/common/PageHeader';

export const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<{
    [page: number]: string | undefined;
  }>({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: tenantsResponse,
    isLoading,
    error,
  } = useQuery<TenantsResponse>({
    queryKey: [
      'tenants',
      includeDeleted,
      debouncedSearchTerm,
      currentPage,
      pageCursors[currentPage],
    ],
    queryFn: () => {
      const cursor = pageCursors[currentPage];
      return partnerApi.getTenants(includeDeleted, {
        limit: 25,
        cursor,
        search: debouncedSearchTerm || undefined,
        ...(cursor ? {} : { orderBy: 'name', orderDirection: 'asc' }),
      });
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Store cursor for next page when data changes
  useEffect(() => {
    if (tenantsResponse?.data?.pagination.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: tenantsResponse.data.pagination.cursor || undefined,
      }));
    }
  }, [tenantsResponse, currentPage]);

  // Reset pagination when search term, includeDeleted, or orderBy changes
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [debouncedSearchTerm, includeDeleted]);

  const tenants = tenantsResponse?.data?.results || [];
  const pagination = tenantsResponse?.data?.pagination;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !debouncedSearchTerm) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <p className="text-red-700">
          Error loading tenants:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (tenants.length === 0 && !isLoading && !debouncedSearchTerm) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-2 text-sm font-semibold text-foreground">
          No Tenants
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Get started by creating a new tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Your Tenants"
        description="A list of all tenants in your organization."
      />

      {/* Search */}
      <div className="relative">
        <Search className="bg-white absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search tenants by name, external ID, or website..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tenants List */}
      <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm p-6 transition-opacity duration-200 opacity-100">
        {error && debouncedSearchTerm ? (
          <div className="text-center py-12">
            <div className="bg-red-50 p-4 rounded-lg inline-block">
              <p className="text-red-700">
                Error loading tenants:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Try adjusting your search terms.
            </p>
          </div>
        ) : tenants.length === 0 && debouncedSearchTerm ? (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-semibold text-foreground">
              No tenants found
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Try adjusting your search terms.
            </p>
          </div>
        ) : (
          <div>
            <ul role="list" className="divide-y divide-border">
              {tenants.map(tenant => (
                <li key={tenant.id}>
                  <button
                    onClick={() => navigate(`/dashboard/tenants/${tenant.id}`)}
                    className={`w-full px-2 py-4 sm:px-4 hover:bg-accent transition-colors text-left ${
                      tenant.deletedAt ? 'opacity-60 bg-accent' : ''
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {tenant.branding?.logoUrl ||
                          tenant.branding?.logoBase64 ? (
                            <img
                              src={
                                tenant.branding.logoUrl ||
                                `data:image/png;base64,${tenant.branding.logoBase64}`
                              }
                              alt={`${tenant.name} logo`}
                              className="h-full w-full object-contain"
                              onError={e => {
                                // Fallback to Building2 icon if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML =
                                    '<svg class="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>';
                                }
                              }}
                            />
                          ) : (
                            <Building2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-foreground">
                              {tenant.name}
                            </h4>
                            {tenant.deletedAt && (
                              <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                                Deactivated
                              </span>
                            )}
                          </div>
                          {tenant.website && (
                            <p className="text-sm text-muted-foreground truncate">
                              {tenant.website}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-xs text-muted-foreground">
                              External ID: {tenant.externalId}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {tenant.userCount.toLocaleString()}{' '}
                              {tenant.userCount === 1 ? 'user' : 'users'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Pagination Controls */}
        {tenants.length > 0 && (
          <div className="border-t border-border px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {(() => {
                  const startItem = (currentPage - 1) * 25 + 1;
                  const endItem = startItem + tenants.length - 1;
                  const total = pagination?.total || 0;

                  if (debouncedSearchTerm) {
                    return `Showing ${tenants.length} results`;
                  }

                  if (tenants.length === 0) {
                    return 'No tenants found';
                  }

                  if (tenants.length === 0) {
                    return 'Loading...';
                  }

                  return `Viewing ${startItem}-${endItem} of ${total} total tenants`;
                })()}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1 || isLoading}>
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!pagination?.hasNextPage || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Next'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer with include deleted toggle */}
        <div className="border-t border-border px-4 py-4 sm:px-6">
          <label className="flex items-center space-x-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={e => setIncludeDeleted(e.target.checked)}
              className="rounded border-input text-primary focus:ring-ring"
            />
            <span>Show deactivated tenants</span>
          </label>
        </div>
      </div>
    </div>
  );
};
